import React, { useState } from "react";
import usePartnerSubscription from "../../hooks/usePartnerSubscription";
import UpgradePopup from "./UpgradePopup";
import Icon from "../Icon";

/**
 * Wraps partner feature pages. If the partner lacks the required service,
 * shows a soft gate + upgrade popup on interaction.
 */
export default function PartnerServiceGate({ serviceKey, featureName, children }) {
  const { loading, hasAccess, hasSubscription, activeServiceKeys } = usePartnerSubscription();
  const [popupOpen, setPopupOpen] = useState(false);

  // Free services / commission model are included in activeServiceKeys by the edge function.
  // Until we have data, don't block.
  const allowed = loading || hasAccess(serviceKey);

  // Soft-launch: if partner has no subscription system rows yet and no keys at all,
  // still allow access so existing partners aren't locked out until they subscribe.
  // Once they have any active keys OR an explicit subscription, enforce gating.
  const enforceGate = hasSubscription || activeServiceKeys.length > 0;

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="w-8 h-8 border-2 border-[#7721E5] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!enforceGate || allowed) {
    return children;
  }

  return (
    <>
      <div className="relative">
        <div className="pointer-events-none select-none opacity-40 blur-[1px]">{children}</div>
        <div className="absolute inset-0 flex items-start justify-center pt-16 sm:pt-24 px-4">
          <div className="w-full max-w-md bg-gradient-to-b from-[#130D1F] to-[#0F0A1A] border border-[#2A2240] rounded-2xl p-6 text-center shadow-2xl">
            <div className="w-12 h-12 mx-auto rounded-xl bg-[#8B3DFF]/15 border border-[#8B3DFF]/30 flex items-center justify-center mb-4">
              <Icon icon="heroicons:lock-closed" className="w-6 h-6 text-[#A855F7]" />
            </div>
            <h3 className="text-white text-lg font-semibold mb-2">Upgrade your plan</h3>
            <p className="text-[#8B85A0] text-sm mb-5">
              {serviceKey === "ugc"
                ? "Subscribe to UGC Creators to browse the creator network, brief campaigns, and chat with matched creators."
                : `Subscribe to ${featureName || serviceKey} to unlock this page.`}
            </p>
            <button
              onClick={() => setPopupOpen(true)}
              className="px-5 py-2.5 rounded-xl bg-[#8B3DFF] hover:bg-[#9B4DFF] text-white text-sm font-semibold transition-colors"
            >
              View subscription options
            </button>
          </div>
        </div>
      </div>
      <UpgradePopup
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
        serviceKey={serviceKey}
        featureName={featureName}
      />
    </>
  );
}
